/**
 * Utility function to get the correct id_path for API calls
 * This helps ensure we're using the right project identifier
 * For multi-tenant system where each project has its own database
 */

export const getProjectIdPath = (): string => {
    // For multi-tenant system, we need to get the REAL id_path, not URL segments
    // Priority order for finding id_path:
    
    // 1. Try from localStorage (most reliable for current session)
    const storedIdPath = localStorage.getItem('current_project_id');
    if (storedIdPath) {
        return storedIdPath;
    }
    
    // 2. Try from session storage
    const sessionIdPath = sessionStorage.getItem('project_id_path');
    if (sessionIdPath) {
        return sessionIdPath;
    }
    
    // 3. Try from meta tags if they contain project info
    const metaProject = document.querySelector('meta[name="project-id"]')?.getAttribute('content');
    if (metaProject) {
        return metaProject;
    }
    
    // 4. Try from URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const queryIdPath = urlParams.get('id_path') || urlParams.get('project');
    if (queryIdPath) {
        return queryIdPath;
    }
    
    // 5. Try from subdomain if using subdomain-based routing
    const hostname = window.location.hostname;
    const subdomain = hostname.split('.')[0];
    if (subdomain && subdomain !== 'www' && subdomain !== 'localhost') {
        return subdomain;
    }
    
    // 6. For multi-tenant: throw error instead of guessing from URL
    // URL segments like 'trung-tam-hoi-cho-trien-lam' are NOT the real id_path
    throw new Error('Cannot determine project id_path. Please ensure project context is properly set.');
};

/**
 * Store the current project id_path for later use
 * Call this when user successfully navigates to a project or logs in
 */
export const setProjectIdPath = (idPath: string): void => {
    localStorage.setItem('current_project_id', idPath);
    sessionStorage.setItem('project_id_path', idPath);
    
    // Also set as meta tag for other functions to use
    let metaTag = document.querySelector('meta[name="project-id"]') as HTMLMetaElement;
    if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = 'project-id';
        document.head.appendChild(metaTag);
    }
    metaTag.content = idPath;
    
    // console.log('Set project id_path:', idPath);
};

/**
 * Get id_path specifically for email verification
 * This function tries to be more aggressive in finding the right path
 * For multi-tenant systems, this is critical to get right
 */
export const getVerificationIdPath = (): string => {
    // For verification, we need to be extra careful to get the right path
    // Since users come from email links and might not have full context
    
    try {
        const idPath = getProjectIdPath();
        
        // Log for debugging
        
        return idPath;
    } catch (error) {
        // For email verification, try to extract from email domain or other sources
        console.error('Cannot determine project id_path:', error);
        
        // Try to get from URL hash or query params if passed
        const urlParams = new URLSearchParams(window.location.search);
        const projectFromParam = urlParams.get('project') || urlParams.get('id_path');
        if (projectFromParam) {
            return projectFromParam;
        }
        
        // Try to extract project ID from token if available
        const token = urlParams.get('token');
        if (token) {
            try {
                // Decode JWT token to check if it contains project info
                const payload = JSON.parse(atob(token.split('.')[1]));
                
                // Check if token contains project info
                if (payload.project_id || payload.id_path) {
                    const projectId = payload.project_id || payload.id_path;
                    return projectId;
                }
            } catch (tokenError) {
                console.error('Failed to decode token:', tokenError);
            }
        }
        
        // Try to get from subdomain if using subdomain-based routing
        const hostname = window.location.hostname;
        const subdomain = hostname.split('.')[0];
        if (subdomain && subdomain !== 'www' && subdomain !== 'localhost') {
            return subdomain;
        }
        
        // For multi-tenant systems, try to use a default project ID based on current setup
        // Check if we have any stored session or project info
        try {
            const storedProject = localStorage.getItem('current_project_id');
            if (storedProject) {
                return storedProject;
            }
        } catch (storageError) {
            console.error('Cannot access localStorage:', storageError);
        }
        
        // As last resort, throw error since we cannot determine project context
        throw new Error('Cannot determine project context. Please access the verification link from the original project.');
    }
};

import { isLocale } from "@/utils/constant/locales";

/**
 * Format a project URL to include locale prefix if locale is non-default (e.g. /ja/ or /en/)
 * Preserves the original domain/URL from DB without modifying the origin.
 */
export const getLocalizedProjectUrl = (url: string | undefined | null, locale?: string): string => {
    if (!url) return '';
    if (url.toLowerCase().includes('sanpham.')) return url;
    if (!locale || locale === 'vn') return url;

    const targetLocale = locale.toLowerCase();
    if (!isLocale(targetLocale)) return url;

    try {
        const isAbsolute = url.startsWith('http://') || url.startsWith('https://');

        if (isAbsolute) {
            const parsed = new URL(url);
            let pathParts = parsed.pathname.split('/').filter(Boolean);
            if (pathParts.length > 0 && isLocale(pathParts[0].toLowerCase())) {
                pathParts.shift();
            }
            pathParts.unshift(targetLocale);
            parsed.pathname = '/' + pathParts.join('/');
            parsed.searchParams.set('lang', targetLocale);
            return parsed.toString();
        } else {
            const cleanUrl = url.startsWith('/') ? url : '/' + url;
            const parsed = new URL(cleanUrl, 'http://localhost');
            let pathParts = parsed.pathname.split('/').filter(Boolean);
            if (pathParts.length > 0 && isLocale(pathParts[0].toLowerCase())) {
                pathParts.shift();
            }
            pathParts.unshift(targetLocale);
            parsed.pathname = '/' + pathParts.join('/');
            parsed.searchParams.set('lang', targetLocale);
            return parsed.pathname + parsed.search + parsed.hash;
        }
    } catch (e) {
        return url;
    }
};